import { z } from "zod";
import { parse, isValid, isBefore, startOfDay } from "date-fns";

// Phone number validation regex - accepts international format (+41...), Swiss format (0...), with spaces, dashes, or parentheses
// Must have at least 8 digits total, can start with +, allows spaces/dashes/parentheses as separators
const phoneRegex = /^[+]?[0-9][\s\-()]*([0-9][\s\-()]*){6,}[0-9]$/;

// Helper function to validate date format and ensure it's a valid date
const validateBirthDate = (value: string) => {
  // First check format
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return false;
  }

  // Try to parse the date
  const parsed = parse(value, "dd/MM/yyyy", new Date());

  // Check if the parsed date is valid
  if (!isValid(parsed)) {
    return false;
  }

  // Check if the date is not in the future (birth date can't be in the future)
  const today = startOfDay(new Date());
  if (!isBefore(parsed, today) && parsed.getTime() !== today.getTime()) {
    return false;
  }

  return true;
};

// Schema for particulier (individual) tenant
const particulierTenantSchema = (t: (key: string) => string, isPpe = false) =>
  z.object({
    status: z.literal("particulier"),
    tenant_index: z.number().optional(), // For maintaining order when submitting
    // Identity & Contact
    civilite: z.enum(["monsieur", "madame", "ne_souhaite_pas_communiquer"], {
      error: t("form.errors.civiliteRequired"),
    }),
    nom: z.string().min(1, t("form.errors.lastNameRequired")),
    prenom: z.string().min(1, t("form.errors.firstNameRequired")),
    date_naissance: isPpe
      ? z.string().optional()
      : z.string().refine((value) => validateBirthDate(value), {
          message: t("form.errors.birthDateInvalid"),
        }),
    nationalite: isPpe
      ? z.string().optional()
      : z.string().min(1, t("form.errors.nationalityRequired")),
    email: z
      .string()
      .min(1, { error: t("form.errors.emailRequired") })
      .pipe(z.email({ error: t("form.errors.emailInvalid") })),
    telephone: z
      .string()
      .trim()
      .transform((val) => (val === "0" ? "" : val))
      .pipe(z.union([z.string().regex(phoneRegex, t("form.errors.phoneInvalid")), z.literal("")]))
      .optional(),
    tel_professionnel: z
      .string()
      .trim()
      .transform((val) => (val === "0" ? "" : val))
      .pipe(z.union([z.string().regex(phoneRegex, t("form.errors.phoneInvalid")), z.literal("")]))
      .optional(),
    tel_portable: z
      .string()
      .trim()
      .transform((val) => (val === "0" ? "" : val))
      .pipe(z.union([z.string().regex(phoneRegex, t("form.errors.phoneInvalid")), z.literal("")]))
      .optional(),
    // Address
    rue: z.string().optional(),
    numero: z.string().optional(),
    npa: z.preprocess(
      (val) => (typeof val === "number" && isNaN(val) ? undefined : val),
      z.number().optional()
    ),
    ville: z.string().optional(),
    correspondance_email: z.boolean().default(true),
    etat_civil: isPpe
      ? z.enum(["celibataire", "marie", "divorce", "veuf"]).optional()
      : z.enum(["celibataire", "marie", "divorce", "veuf"]).default("celibataire"),
    is_guarantor: z.boolean().default(false),
  });

// Schema for entreprise (company) tenant
const entrepriseTenantSchema = (t: (key: string) => string) =>
  z.object({
    status: z.literal("entreprise"),
    tenant_index: z.number().optional(), // For maintaining order when submitting
    // Company information
    nom_societe: z.string().min(1, t("form.errors.companyNameRequired")),
    civilite: z.enum(["monsieur", "madame", "ne_souhaite_pas_communiquer"], {
      error: t("form.errors.civiliteRequired"),
    }),
    tel_professionnel: z
      .string()
      .trim()
      .transform((val) => (val === "0" ? "" : val))
      .pipe(z.union([z.string().regex(phoneRegex, t("form.errors.phoneInvalid")), z.literal("")]))
      .optional(),
    tel_portable: z
      .string()
      .trim()
      .transform((val) => (val === "0" ? "" : val))
      .pipe(z.union([z.string().regex(phoneRegex, t("form.errors.phoneInvalid")), z.literal("")]))
      .optional(),
    // Contact person (representant fields)
    nom_representant: z.string().min(1, t("form.errors.lastNameRequired")),
    prenom_representant: z.string().min(1, t("form.errors.firstNameRequired")),
    email_representant: z
      .string()
      .min(1, { error: t("form.errors.emailRequired") })
      .pipe(z.email({ error: t("form.errors.emailInvalid") })),
    // Address
    rue: z.string().optional(),
    numero: z.string().optional(),
    npa: z.preprocess(
      (val) => (typeof val === "number" && isNaN(val) ? undefined : val),
      z.number().optional()
    ),
    ville: z.string().optional(),
    correspondance_email: z.boolean().default(true),
    is_guarantor: z.boolean().default(false),
  });

// Union of tenant schemas
const tenantSchema = (t: (key: string) => string, isPpe = false) =>
  z.discriminatedUnion("status", [particulierTenantSchema(t, isPpe), entrepriseTenantSchema(t)]);

export const createResidentFormSchema = (t: (key: string) => string, isPpe = false) =>
  z
    .object({
      // Separate arrays for individual and enterprise tenants
      individual_tenants: z.array(particulierTenantSchema(t, isPpe)),
      enterprise_tenants: z.array(entrepriseTenantSchema(t)),
      // Shared fields (outbuilding, bank, insurance, documents)
      cave: z.string().optional(),
      parking: z.string().optional(),
      grenier: z.string().optional(),

      // Bank & Insurance
      nom_banque: z.string().optional(),
      titulaire_compte: z.string().optional(),
      iban: z.string().optional(),
      nom_assurance_rc: isPpe
        ? z.string().optional()
        : z.string().min(1, t("form.errors.insuranceRequired")),
      numero_police_rc: isPpe
        ? z.string().optional()
        : z.string().min(1, t("form.errors.policyRequired")),

      // Comment
      comment: z.string().optional(),

      // PPE-specific fields
      numero_appartement: z.string().optional(),
      autre: z.string().optional(),
      clearing: z.string().optional(),
      locataire_nom: z.string().optional(),
      locataire_prenom: z.string().optional(),
      locataire_tel_prive: z
        .string()
        .trim()
        .transform((val) => (val === "0" ? "" : val))
        .pipe(z.union([z.string().regex(phoneRegex, t("form.errors.phoneInvalid")), z.literal("")]))
        .optional(),
      locataire_tel_portable: z
        .string()
        .trim()
        .transform((val) => (val === "0" ? "" : val))
        .pipe(z.union([z.string().regex(phoneRegex, t("form.errors.phoneInvalid")), z.literal("")]))
        .optional(),
      locataire_gerance: z.string().optional(),
      moyen_paiement: z.string().optional(),

      // PPE flag (injected from form meta to inform validation)
      is_ppe: z.boolean().optional(),

      // Legacy fields (for backward compatibility)
      status: z.enum(["particulier", "entreprise"]).optional(),
      adresse: z.string().optional(),
      adresse_correspondance: z.string().optional(),
      name: z.string().optional(),
      phone: z.string().optional(),
      preferred_contact_time: z.string().optional(),
      comments: z.string().optional(),
    })
    .refine((data) => data.individual_tenants.length + data.enterprise_tenants.length >= 1, {
      message: t("form.errors.atLeastOneTenant"),
      path: ["individual_tenants"], // Show error on individual_tenants field
    })
    .refine(
      (data) => {
        // In PPE mode, skip guarantor requirement (no guarantor concept)
        if (data.is_ppe) return true;
        // Check that at least one tenant is not a guarantor
        const hasNonGuarantor =
          data.individual_tenants.some((tenant) => !tenant.is_guarantor) ||
          data.enterprise_tenants.some((tenant) => !tenant.is_guarantor);
        return hasNonGuarantor;
      },
      {
        message: t("form.errors.atLeastOneNonGuarantor"),
        path: ["individual_tenants"],
      }
    );

export type ResidentFormData = z.infer<ReturnType<typeof createResidentFormSchema>>;
export type TenantData = z.infer<ReturnType<typeof tenantSchema>>;
